import { forwardRef } from 'react';
import { Input as AntdInput } from 'antd';
import { CloseCircleBoldIcon } from '@solar-icons/react';
import './Input.module.css';

const ROOT_STYLE = { minHeight: 44, fontSize: 16, alignItems: 'center' };
const INNER_STYLE = { fontSize: 16 };
const CLEAR_ICON = <CloseCircleBoldIcon size={24} color="var(--color-neutral-550)" aria-label="Clear" />;

const withClearIcon = (allowClear) => {
    if (!allowClear) return allowClear;
    return { clearIcon: CLEAR_ICON, ...(allowClear === true ? {} : allowClear) };
};

/**
 * @param {Object} props - Same shape as antd's own `Input`.
 * @param {Object} [props.styles] - `styles.root`/`styles.input` merge with a 44px-tall,
 *   16px-font sizing baseline; pass your own to override just those keys.
 * @param {boolean|Object} [props.allowClear] - Shows a clear button with an accessible label;
 *   pass `{ clearIcon }` to use your own icon instead of the default.
 */
const Input = forwardRef(({ styles, allowClear, ...props }, ref) => (
    <AntdInput
        ref={ref}
        styles={{
            ...styles,
            root: { ...ROOT_STYLE, ...styles?.root },
            input: { ...INNER_STYLE, ...styles?.input },
        }}
        allowClear={withClearIcon(allowClear)}
        {...props}
    />
));
Input.displayName = 'Input';

/** @param {Object} props - Same as `Input`'s, applied to antd's own `Input.TextArea`. */
const TextArea = forwardRef(({ styles, allowClear, ...props }, ref) => (
    <AntdInput.TextArea
        ref={ref}
        styles={{
            ...styles,
            root: { ...ROOT_STYLE, ...styles?.root },
            textarea: { ...INNER_STYLE, ...styles?.textarea },
        }}
        allowClear={withClearIcon(allowClear)}
        {...props}
    />
));
TextArea.displayName = 'Input.TextArea';

/** @param {Object} props - Same as `Input`'s, applied to antd's own `Input.Password`. */
const Password = forwardRef(({ styles, allowClear, ...props }, ref) => (
    <AntdInput.Password
        ref={ref}
        styles={{
            ...styles,
            root: { ...ROOT_STYLE, ...styles?.root },
            input: { ...INNER_STYLE, ...styles?.input },
        }}
        allowClear={withClearIcon(allowClear)}
        {...props}
    />
));
Password.displayName = 'Input.Password';

Input.TextArea = TextArea;
Input.Password = Password;

export default Input;
